import { useMutation, useQueryClient } from '@tanstack/react-query';

import { getCurrWorkspaceAlias } from '@/utils/workspace';

import type { Issue } from '../model/types';
import { favoriteIssue } from './favorite-issue';
import { hideIssue } from './hide-issue';
import { issueQueryKeys } from './issue.queries';

/**
 * 이슈 즐겨찾기 mutation
 * Optimistic update 패턴 적용 - 랭킹 변경 없이 favorite 상태만 즉시 업데이트
 */
export function useFavoriteIssueMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: favoriteIssue,
    onMutate: async (variables) => {
      const workspaceId = getCurrWorkspaceAlias();
      const queryKey = issueQueryKeys.list(workspaceId);

      // 진행 중인 쿼리 취소
      await queryClient.cancelQueries({ queryKey });

      // 롤백을 위해 이전 데이터 스냅샷 저장
      const previousData = queryClient.getQueryData<Issue[]>(queryKey);

      // 캐시 optimistic 업데이트
      if (previousData) {
        queryClient.setQueryData<Issue[]>(
          queryKey,
          previousData.map((issue) =>
            issue.name === variables.name
              ? { ...issue, favorite: variables.favorite }
              : issue,
          ),
        );
      }

      return { previousData, queryKey };
    },
    onError: (_err, _variables, context) => {
      // 에러 시 이전 데이터로 롤백
      if (context?.previousData && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousData);
      }
    },
  });
}

/**
 * 이슈 숨기기 mutation
 */
export function useHideIssueMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: hideIssue,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: issueQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: issueQueryKeys.aggregates() });
    },
  });
}
